import {
	createFileRoute,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import z from "zod";
import type { SearchPostsQuery } from "./__generated__/SearchPostsQuery.graphql";
import type { search_posts$key } from "./__generated__/search_posts.graphql";
import type { searchQuery } from "./__generated__/searchQuery.graphql";
import { useForm } from "@tanstack/react-form";
import { Heading } from "@/components/heading";
import { Field } from "@/components/ui/field";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { Icon } from "@/components/icon";
import { useEffect, useTransition } from "react";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { Spinner } from "@/components/ui/spinner";
import { PostPreview } from "@/ui/post-preview";

export const Route = createFileRoute("/_visibility/_topnav/search")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			query: z.string().optional(),
		}),
	),
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const navigate = useNavigate({ from: Route.fullPath });
	const { query } = useSearch({ from: Route.id });

	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const [isPending, startTransition] = useTransition();
	const { track } = useAnalytics();

	const form = useForm({
		defaultValues: {
			query: query || "",
		},
		onSubmit: ({ value }) => {
			track(AnalyticsEvents.POST_SEARCHED, { query: value.query });
			startTransition(() => {
				navigate({
					search: (s) => ({
						...s,
						query: value.query,
					}),
				});
			});
		},
	});

	const data = useLazyLoadQuery<searchQuery>(
		graphql`
	    query searchQuery($club: String!, $query: String!) {
				club(slug: $club) {
					id
				}
				...search_posts @arguments(club: $club, query: $query)
			}
	`,
		{
			club: club.slug,
			query: query || "",
		},
	);

	const {
		data: posts,
		hasNext,
		loadNext,
		isLoadingNext,
	} = usePaginationFragment<SearchPostsQuery, search_posts$key>(
		graphql`
	  fragment search_posts on Query
		@argumentDefinitions(
		  query: { type: "String!" }
			club: { type: "String!" }
			first: { type: "Int" }
			cursor: { type: "String" }
		)
		@refetchable(queryName: "SearchPostsQuery")
		{
			searchPost(club: $club, query: $query, first: $first, after: $cursor)
			@connection(key: "posts_searchPost")
			{
			  edges {
			    node {
						id
				    ...postPreviewFragment @arguments(club: $club)
			    }
			  }
			  pageInfo {
			    hasNextPage
			  }
			}
		}
	`,
		data,
	);

	useEffect(() => {
		if (
			entry?.isIntersecting &&
			(query?.length || 0) > 0 &&
			hasNext &&
			!isLoadingNext
		) {
			loadNext(10);
		}
	}, [entry?.isIntersecting, hasNext, query, isLoadingNext, loadNext]);

	return (
		<div className="w-full flex items-start justify-center p-6">
			<form
				className="w-full max-w-[600px] flex flex-col gap-6"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<Heading>Pesquisar Posts</Heading>

				<form.Field name="query">
					{(field) => (
						<Field>
							<InputGroup>
								<InputGroupInput
									placeholder="Pesquisar..."
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>
								<InputGroupAddon>
									<Icon name="Search" />
								</InputGroupAddon>
							</InputGroup>
						</Field>
					)}
				</form.Field>

				{posts.searchPost.edges?.filter(Boolean).map(({ node }) => (
					<PostPreview key={node.id} post={node} />
				))}

				<div ref={ref} className="size-1" />

				{isPending && <Spinner />}
			</form>
		</div>
	);
}
